import { BusinessResponse, UpdateBusinessRequestBody } from 'compliance-api';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useState } from 'react';
import * as yup from 'yup';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import { useUpdateBusinessMutation } from '@/modules/admin-dashboard/queries/businesses.ts';
import { BaseModalProps, Button, Dropzone, ModalContentWrapper, TextInputField } from 'ui';

export const REGISTER_OFFICE_REGEX = /^[A-Za-z\d\s/-]+,\s*[A-Za-z\d\s/-]+$/;

const validationSchema = yup.object<UpdateBusinessRequestBody>({
  name: yup.string().min(3).max(100).required('Organisation name is required'),
  icon_base64: yup.string().trim().nullable(),
  registration_address: yup
    .string()
    .trim()
    .matches(
      REGISTER_OFFICE_REGEX,
      'Incorrect address, example of correct one: 111/11 Name Street, North Sydney NSW 1111',
    )
    .required('This field is required'),
  email: yup
    .string()
    .trim()
    .email('Email must be a valid email')
    .required('This field is required'),
});

interface BusinessModalProps extends BaseModalProps {
  initBusiness: BusinessResponse;
}

const BusinessModal = ({ initBusiness, open, onClose }: BusinessModalProps) => {
  const [iconUrl, setIconUrl] = useState(initBusiness.icon_url || '');
  const [isFileLoading, setIsFileLoading] = useState(false);
  const formik = useFormik<UpdateBusinessRequestBody & { id: number }>({
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    enableReinitialize: true,
    initialValues: {
      id: initBusiness.id,
      icon_base64: undefined,
      email: initBusiness.email,
      registration_address: initBusiness.registration_address,
      name: initBusiness.name,
    },
    onSubmit: async values => {
      await mutateAsync(values);
    },
  });

  const { mutateAsync, isLoading } = useUpdateBusinessMutation({
    setErrors: formik.setErrors,
    onSuccess: onClose,
  });

  const handleChangeSelect = useCallback(
    (field: keyof UpdateBusinessRequestBody) => async (option: string | boolean | null | any) => {
      await formik.setFieldValue(field, option);
      formik.setFieldError(field, '');
    },
    [],
  );

  const onFileDelete = useCallback(async () => {
    await handleChangeSelect('icon_base64')(null);
    setIconUrl('');
    await mutateAsync({
      ...formik.values,
      icon_base64: '',
    });
  }, [formik.values, handleChangeSelect, mutateAsync]);

  const onFileAccepted = useCallback(
    async (file: File) => {
      setIsFileLoading(true);

      const base64 = await fileToBase64(file, true);

      await handleChangeSelect('icon_base64')(base64);

      setIsFileLoading(false);
    },
    [handleChangeSelect],
  );

  return (
    <ModalContentWrapper title="Update organisation" open={open} onClose={onClose}>
      <FormikProvider value={formik}>
        <div className="space-y-10">
          <div className="space-y-8">
            <TextInputField name="name" label="Name" />

            <div className="w-full flex flex-col items-start justify-center gap-2">
              <div className="flex flex-col justify-center text-left gap-1">
                <span className="font-[Inter] text-[10px] leading-[18px] text-gray-900 text-opacity-[.64] font-medium uppercase">
                  Image
                </span>
              </div>
              <div className="w-full">
                <Dropzone
                  variant="icon"
                  changeExpansionImage={true}
                  acceptFormat={['jpeg', 'png']}
                  maxSizeMb={1}
                  onFileAccepted={onFileAccepted}
                  loading={isFileLoading}
                  value={formik.values.icon_base64 || iconUrl}
                  onFileClear={onFileDelete}
                  error={formik.errors.icon_base64 as string}
                />
              </div>
            </div>

            <TextInputField
              name="email"
              label="E-mail"
              placeholder="Organization's email address"
            />

            <TextInputField name="registration_address" label="Registration Address" />
          </div>

          <Button disabled={isLoading} loading={isLoading} onClick={formik.submitForm}>
            Update
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default BusinessModal;
